import React from 'react';
import PropTypes from 'prop-types';

/**
 * 통계 카드 컴포넌트
 * 숫자나 통계 정보를 표시하는 카드
 */
export const StatCard = ({ 
  title, 
  value,
  subtitle,
  icon,
  trend, // { value: number, isIncrease: boolean }
  color = 'blue', // 'blue', 'green', 'red', 'yellow', 'purple', 'gray'
  onClick,
  className = ''
}) => {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      value: 'text-blue-900',
      border: 'border-blue-200'
    },
    green: {
      bg: 'bg-green-50',
      icon: 'text-green-600',
      value: 'text-green-900',
      border: 'border-green-200'
    },
    red: {
      bg: 'bg-red-50',
      icon: 'text-red-600',
      value: 'text-red-900',
      border: 'border-red-200'
    },
    yellow: {
      bg: 'bg-yellow-50',
      icon: 'text-yellow-600',
      value: 'text-yellow-900',
      border: 'border-yellow-200'
    },
    purple: {
      bg: 'bg-purple-50',
      icon: 'text-purple-600',
      value: 'text-purple-900',
      border: 'border-purple-200'
    },
    gray: {
      bg: 'bg-gray-50',
      icon: 'text-gray-600',
      value: 'text-gray-900',
      border: 'border-gray-200'
    }
  };

  const classes = colorClasses[color];
  const isClickable = !!onClick;

  return (
    <div 
      className={`rounded-3xl border bg-white shadow-lg ${classes.border} p-4 ${
        isClickable ? 'cursor-pointer transition-shadow duration-200 hover:shadow-xl' : ''
      } ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {/* 제목 */}
          <p className="mb-1 text-sm font-medium text-gray-600">{title}</p>
          
          {/* 값 */}
          <p className={`text-2xl font-bold ${classes.value} mb-1`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          
          {/* 부제목/추가 정보 */}
          {subtitle && (
            <p className="text-xs text-gray-500">{subtitle}</p>
          )}
          
          {/* 트렌드 */}
          {trend && (
            <div className="mt-2 flex items-center">
              <span className={`text-xs font-medium ${
                trend.isIncrease ? 'text-green-600' : 'text-red-600'
              }`}>
                {trend.isIncrease ? '▲' : '▼'} {Math.abs(trend.value)}
                {trend.unit || '%'}
              </span>
              <span className="ml-1 text-xs text-gray-500">전 기간 대비</span>
            </div>
          )}
        </div>
        
        {/* 아이콘 */}
        {icon && (
          <div className={`${classes.bg} rounded-3xl p-3 ${classes.icon}`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 진행률 카드 컴포넌트
 * 진행률이나 퍼센티지를 표시하는 카드
 */
export const ProgressCard = ({ 
  title, 
  current,
  total,
  percentage,
  color = 'blue',
  subtitle,
  className = ''
}) => {
  const calculatedPercentage = percentage !== undefined 
    ? percentage 
    : total > 0 ? Math.round((current / total) * 100) : 0;

  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500'
  };

  return (
    <div className={`rounded-3xl bg-white p-4 shadow-lg ${className}`}>
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-600">{title}</h3>
          <span className="text-sm font-bold text-gray-900">{calculatedPercentage}%</span>
        </div>
        
        {/* 진행률 바 */}
        <div className="h-2 w-full rounded-full bg-gray-200">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${colorClasses[color]}`}
            style={{ width: `${calculatedPercentage}%` }}
          ></div>
        </div>
      </div>
      
      {/* 현재/전체 정보 */}
      {(current !== undefined && total !== undefined) && (
        <div className="flex justify-between text-xs text-gray-500">
          <span>현재: {current.toLocaleString()}</span>
          <span>전체: {total.toLocaleString()}</span>
        </div>
      )}
      
      {/* 부제목 */}
      {subtitle && (
        <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
      )}
    </div>
  );
};

/**
 * 다중 통계 카드 컴포넌트
 * 여러 개의 통계를 한 카드에 표시
 */
export const MultiStatCard = ({ 
  title, 
  stats = [], // [{ label, value, color }]
  className = ''
}) => {
  return (
    <div className={`rounded-3xl bg-white p-4 shadow-lg ${className}`}>
      {title && (
        <h3 className="mb-3 text-sm font-medium text-gray-600">{title}</h3>
      )}
      
      <div className="space-y-3">
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{stat.label}</span>
            <span className={`text-sm font-bold ${
              stat.color === 'green' ? 'text-green-600' :
              stat.color === 'red' ? 'text-red-600' :
              stat.color === 'blue' ? 'text-blue-600' :
              stat.color === 'yellow' ? 'text-yellow-600' :
              'text-gray-900'
            }`}>
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
  trend: PropTypes.shape({
    value: PropTypes.number.isRequired,
    isIncrease: PropTypes.bool.isRequired,
    unit: PropTypes.string
  }),
  color: PropTypes.oneOf(['blue', 'green', 'red', 'yellow', 'purple', 'gray']),
  onClick: PropTypes.func,
  className: PropTypes.string
};

ProgressCard.propTypes = {
  title: PropTypes.string.isRequired,
  current: PropTypes.number,
  total: PropTypes.number,
  percentage: PropTypes.number,
  color: PropTypes.oneOf(['blue', 'green', 'red', 'yellow', 'purple']),
  subtitle: PropTypes.string,
  className: PropTypes.string
};

MultiStatCard.propTypes = {
  title: PropTypes.string,
  stats: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    color: PropTypes.string
  })),
  className: PropTypes.string
}; 